// ─── GuidedTour.jsx — Interactive step-by-step guided tour ───────────────────
// Highlights UI elements with red glowing border and shows modal cards with explanations.
// Usage: <GuidedTour steps={STEPS} active={showTour} onClose={()=>setShowTour(false)} />

import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";

// ─── Tour step definition format ────────────────────────────────────────────
// {
//   target: "#element-id" or "[data-tour='name']",   // CSS selector
//   title: "Step Title",
//   description: "What this does and why it matters.",
//   position: "bottom" | "top" | "left" | "right",   // where the card appears
// }

export default function GuidedTour({ steps = [], active = false, onClose }) {
  const T = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const animFrame = useRef(null);
  const activeRef = useRef(active);
  const scrollTimeouts = useRef([]);
  activeRef.current = active;

  const step = steps[currentStep];

  // Compute clamped card position given a target rect and real card size
  const computeCardPos = useCallback((rect, pos, cardW, cardH) => {
    const pad = 16;
    let top, left;
    if (pos === "bottom") {
      top = rect.bottom + pad;
      left = rect.left + rect.width / 2 - cardW / 2;
    } else if (pos === "top") {
      top = rect.top - cardH - pad;
      left = rect.left + rect.width / 2 - cardW / 2;
    } else if (pos === "left") {
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.left - cardW - pad;
    } else {
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.right + pad;
    }
    // Clamp to viewport with 12px margin
    left = Math.max(12, Math.min(left, window.innerWidth - cardW - 12));
    top = Math.max(12, Math.min(top, window.innerHeight - cardH - 12));
    return { top, left };
  }, []);

  // After card renders, re-measure its real height and re-clamp position
  useEffect(() => {
    if (!targetRect || !cardRef.current) return;
    cancelAnimationFrame(animFrame.current);
    animFrame.current = requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) return;
      const realH = card.offsetHeight;
      const realW = card.offsetWidth || 340;
      const pos = step?.position || "bottom";
      const clamped = computeCardPos(targetRect, pos, realW, realH);
      // Only update if significantly different to avoid infinite loops
      if (Math.abs(clamped.top - cardPos.top) > 2 || Math.abs(clamped.left - cardPos.left) > 2) {
        setCardPos(clamped);
      }
    });
    return () => cancelAnimationFrame(animFrame.current);
  }, [targetRect, step?.position, computeCardPos]);

  // Find and measure the target element
  const measureTarget = useCallback(() => {
    if (!step?.target || !active || !activeRef.current) return;
    const el = document.querySelector(step.target);
    if (el) {
      // Scroll element into view if not visible
      const rect0 = el.getBoundingClientRect();
      if (rect0.top < 0 || rect0.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const tid = setTimeout(() => { if (activeRef.current) measureTarget(); }, 400);
        scrollTimeouts.current.push(tid);
        return;
      }
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      // Elevate element above backdrop
      el.dataset.tourActive = "true";
      if (!el.dataset.tourOrigPosition) el.dataset.tourOrigPosition = el.style.position || "";
      if (!el.dataset.tourOrigZindex) el.dataset.tourOrigZindex = el.style.zIndex || "";
      el.style.position = el.style.position || "relative";
      el.style.zIndex = "10001";

      // Initial position with estimated card height (refined after render)
      const cardW = 340;
      const cardH = 200;
      setCardPos(computeCardPos(rect, step.position || "bottom", cardW, cardH));
    } else {
      setTargetRect(null);
    }
  }, [step, active, computeCardPos]);

  // Measure on step change and on scroll/resize
  useEffect(() => {
    if (!active) return;
    // Run onEnter callback if defined (e.g. switch view mode, scroll to section)
    if (step?.onEnter) {
      step.onEnter();
      // Wait for DOM to update after onEnter action, then measure
      const t = setTimeout(() => measureTarget(), 350);
      const onResize = () => measureTarget();
      window.addEventListener("resize", onResize);
      window.addEventListener("scroll", onResize, true);
      return () => {
        clearTimeout(t);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("scroll", onResize, true);
      };
    }
    measureTarget();
    const onResize = () => measureTarget();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [active, currentStep, measureTarget]);

  // Clean up z-index on step change
  useEffect(() => {
    return () => {
      document.querySelectorAll("[data-tour-active]").forEach(el => {
        el.style.position = el.dataset.tourOrigPosition || "";
        el.style.zIndex = el.dataset.tourOrigZindex || "";
        delete el.dataset.tourOrigPosition;
        delete el.dataset.tourOrigZindex;
        delete el.dataset.tourActive;
      });
    };
  }, [currentStep, active]);

  // Clean up everything on close
  useEffect(() => {
    if (!active) {
      scrollTimeouts.current.forEach(tid => clearTimeout(tid));
      scrollTimeouts.current = [];
      document.querySelectorAll("[data-tour-active]").forEach(el => {
        el.style.position = el.dataset.tourOrigPosition || "";
        el.style.zIndex = el.dataset.tourOrigZindex || "";
        delete el.dataset.tourOrigPosition;
        delete el.dataset.tourOrigZindex;
        delete el.dataset.tourActive;
      });
      setCurrentStep(0);
      setTargetRect(null);
    }
  }, [active]);

  function next() {
    if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
    else onClose?.();
  }
  function prev() {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  }
  function skip() { onClose?.(); }

  if (!active || !step) return null;

  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  return (
    <>
      {/* Global style for the glow animation */}
      <style>{`
        @keyframes sfTourGlow {
          0%, 100% { box-shadow: 0 0 0 3px rgba(239,68,68,0.5), 0 0 16px rgba(239,68,68,0.3), 0 0 32px rgba(239,68,68,0.1); }
          50% { box-shadow: 0 0 0 5px rgba(239,68,68,0.7), 0 0 24px rgba(239,68,68,0.5), 0 0 48px rgba(239,68,68,0.2); }
        }
      `}</style>

      {/* Backdrop — transparent click-to-close area, NO dimming */}
      <div onClick={skip} style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "transparent",
        pointerEvents: "auto",
      }}/>

      {/* Glow ring — fixed-position overlay, never clipped by parent overflow */}
      {targetRect && (
        <div style={{
          position: "fixed",
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          borderRadius: 14,
          zIndex: 10001,
          pointerEvents: "none",
          animation: "sfTourGlow 2s ease-in-out infinite",
        }}/>
      )}

      {/* Modal card */}
      <div ref={cardRef} style={{
        position: "fixed",
        top: cardPos.top,
        left: cardPos.left,
        zIndex: 10002,
        width: 340,
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.xl,
        boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.1)`,
        padding: "20px 22px",
        fontFamily: FONT,
        animation: "sfFadeIn 0.25s ease both",
      }}>
        {/* Step counter */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: i === currentStep ? 20 : 6, height: 6,
                borderRadius: 3,
                background: i === currentStep ? "#ef4444" : i < currentStep ? T.accent : T.border,
                transition: "all 0.3s cubic-bezier(0.34,1.3,0.64,1)",
              }}/>
            ))}
          </div>
          <span style={{ fontSize: 11, color: T.textSub }}>{currentStep + 1} / {steps.length}</span>
        </div>

        {/* Title */}
        <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 8, letterSpacing: -0.3 }}>
          {step.title}
        </div>

        {/* Description */}
        <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.7, marginBottom: 20 }}>
          {step.description}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {!isFirst && (
            <button onClick={prev} style={{
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md, color: T.textSub, padding: "9px 16px",
              fontSize: 13, fontFamily: FONT, cursor: "pointer",
            }}>← Back</button>
          )}
          <div style={{ flex: 1 }}/>
          <button onClick={skip} style={{
            background: "none", border: "none", color: T.textSub,
            fontSize: 12, fontFamily: FONT, cursor: "pointer",
          }}>Skip tour</button>
          <button onClick={next} style={{
            background: isLast ? "linear-gradient(135deg,#8b5cf6,#06b6d4)" : "#ef4444",
            border: "none", borderRadius: RADIUS.md, color: "#fff",
            padding: "9px 20px", fontSize: 13, fontWeight: 700,
            fontFamily: FONT, cursor: "pointer",
            boxShadow: isLast ? "0 4px 16px rgba(139,92,246,0.4)" : "0 4px 16px rgba(239,68,68,0.3)",
          }}>{isLast ? "✓ Done" : "Next →"}</button>
        </div>
      </div>
    </>
  );
}

// ─── Tour Button — renders the ? tutorial trigger on each page ──────────────
export function TourButton({ onClick }) {
  const T = useTheme();
  return (
    <button onClick={onClick} data-tour="tour-button" title="Start guided tour"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
        borderRadius: RADIUS.pill, padding: "5px 14px",
        color: "#ef4444", fontSize: 12, fontWeight: 700,
        fontFamily: FONT, cursor: "pointer",
        transition: "all 0.15s cubic-bezier(0.34,1.3,0.64,1)",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.18)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.4)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(239,68,68,0.1)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.25)"; }}
    >
      🎓 Tour
    </button>
  );
}

// ─── Pre-built tour definitions per page ─────────────────────────────────────
export const TOURS = {
  dashboard: [
    { target: "[data-tour='nav-bar']", title: "Navigation", description: "Switch between pages here. Each icon is a section of CashHorizon — Dashboard, Cash Calendar, Targets, Planner, Markets, Supplier Orders, Recurring, Modifiers, Export & Import, and Help. The active page is highlighted.", position: "bottom" },
    { target: "[data-tour='tour-button']", title: "Guided Tour", description: "You're using it right now! Every page has its own tour. Click this button anytime to replay the tutorial for the current page. New features get added to tours automatically.", position: "bottom" },
    { target: "[data-tour='kpi-cards']", title: "KPI Cards", description: "Your key numbers at a glance — Available Cash, Year-End Balance, Blended Payout/Unit, Next Payment Due, Runway (days until cash runs out), and Units Left this month. Each card has an info icon with more detail. Update 'Available Today' from your real bank balance for accurate projections.", position: "bottom" },
    { target: "[data-tour='chart-controls']", title: "Chart Controls & Alerts", description: "Overdue payments, upcoming dues, marketplace filter, view mode (Day/Week/Month), time range, Planner toggle, Orders overlay, PDF export, and CSV export — all in one bar. Red badges mean something needs your attention.", position: "bottom" },
    { target: "[data-tour='chart']", title: "Cashflow Chart", description: "Your projected cash balance over time. The line shows where your money is heading based on all your data — targets, payouts, orders, expenses, and modifiers. Decision dots and order markers appear when toggled on. The 'Today' line shows where you are right now.", position: "top" },
    { target: "[data-tour='monthly-table']", title: "Monthly Breakdown", description: "Every month's income, expenses, running balance, and month-over-month change (Δ). Green numbers mean positive cashflow, red means you're spending more than earning. Click any row to jump to that month in Cash Calendar for the full breakdown.", position: "top" },
    { target: "[data-tour='calculator']", title: "Calculator", description: "A quick-access calculator for when you need to crunch numbers without leaving the app. Click the 🧮 button to open it, do your math, and close it — no tab-switching needed.", position: "top" },
    { target: "[data-tour='chat-widget']", title: "AI Chat Assistant", description: "Your personal cashflow AI. Ask anything about your data — 'What's my projected balance in September?', 'Which month has the highest expenses?', or run what-if simulations like 'What if I'm out of stock for 2 weeks?'. It reads your live data to give real answers.", position: "top" },
  ],

  targets: [
    { target: "[data-tour='targets-chart']", title: "Target vs Actual Chart", description: "Visual comparison of your planned targets (dashed purple) vs actual units sold (solid green). Diamond markers show when order batches arrive in stock. The 'Today' dashed line marks the current week. Toggle Stock to see inventory depletion over time.", position: "bottom" },
    { target: "[data-tour='targets-chart-controls']", title: "Chart Controls", description: "Month/Week view toggle, Stock inventory line, Orders dropdown (show arrival diamonds for all or specific orders), and year selector. Switch years to compare performance across different periods.", position: "bottom" },
    { target: "[data-tour='year-current']", title: "Current Year", description: "Your monthly unit targets for the current year. Each card shows the target number — tap to edit. Green 'SOLD' values track actual performance. The sparkline in the header shows the shape of your year. The summary row shows total target, sold, average, best month, and hit rate %.", position: "top" },
    { target: "[data-tour='year-next']", title: "Next Year & Copy", description: "Plan ahead by setting targets for future years. Use 'Copy' to duplicate last year's targets with an optional growth percentage — e.g. +20% applies a 1.2× multiplier to every month. Orange '+200% units' badges show where modifiers are active.", position: "top" },
  ],

  marketplaces: [
    {
      target: "[data-tour='mkt-carousel']",
      title: "Your Marketplaces",
      description: "Each card is a sales channel — Amazon, Shopify, eBay, eMAG, etc. Shows the marketplace name, % of total sales, payout per unit, and product count. Click a card to select it and configure its settings below. Use arrow keys or swipe to scroll through them.",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='fulfillment-costs']",
      title: "Fulfillment Costs",
      description: "Open the fulfilment cost manager — set per-unit warehouse, pick-and-pack, and 3PL fees for each marketplace. These costs automatically generate monthly expense entries on your Cash Calendar based on your unit targets.",
      position: "bottom",
    },
    {
      target: "[data-tour='add-marketplace']",
      title: "Add Marketplace",
      description: "Create a new sales channel. Enter the marketplace name, payout delay (days until you get paid), and percentage of your total revenue. Each marketplace you add becomes a new income source in your cashflow projections.",
      position: "bottom",
    },
    {
      target: "[data-tour='mkt-import']",
      title: "Import",
      description: "Upload a CSV file from your marketplace — Amazon transactions, Shopify payouts, or a custom format. The system auto-detects columns and calculates your actual payout per unit and sales distribution.",
      position: "bottom",
      onEnter: () => {
        const el = document.querySelector("[data-tour='mkt-import']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      },
    },
    {
      target: "[data-tour='mkt-add-product']",
      title: "Add Product",
      description: "Quick-add a product to the selected marketplace. Set the product name, payout per unit, and percentage of this marketplace's sales. In Advanced view you can also configure delivery cost, PPC, refund rates, and COGS.",
      position: "bottom",
    },
    {
      target: "[data-tour='mkt-launch-product']",
      title: "Launch Product",
      description: "Full launch wizard for a new product across one or multiple marketplaces. Set up payout, COGS breakdown, and profitability in one flow — the product is added to every marketplace you select.",
      position: "bottom",
    },
    {
      target: "[data-tour='mkt-product-list']",
      title: "Product List",
      description: "All products in the selected marketplace. Each row shows the product name, payout per unit, and share of marketplace sales. Click to expand and edit payout, delivery cost, PPC, refund rates, and COGS.",
      position: "top",
    },
  ],

  orders: [
    {
      target: "[data-tour='order-carousel']",
      title: "Your Orders",
      description: "Each card is a supplier purchase order showing its name, total amount, and status. Click one to see its full details below — payment terms, container batches, and shipping timeline. Use arrow keys or swipe to scroll through them.",
      position: "bottom",
      onEnter: () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Ensure we're in timeline view
        const toggleBtns = document.querySelectorAll("[data-tour='order-view-toggle'] button");
        for (const b of toggleBtns) {
          if (b.textContent.includes("Timeline")) { b.click(); break; }
        }
      },
    },
    {
      target: "[data-tour='order-view-toggle']",
      title: "Timeline & Board View",
      description: "Switch between Timeline view (visual order flow with detailed cards) and Board view (Kanban columns: Pending → In Transit → Arrived → Complete). Board view lets you see all orders grouped by status at a glance.",
      position: "bottom",
    },
    {
      target: "[data-tour='add-order']",
      title: "Add Order",
      description: "Create a new supplier purchase order. In Simple mode, just enter a name, amount, and date. In Advanced mode, set payment term splits (e.g. 30/70), container batches with departure and arrival dates, and unit quantities.",
      position: "bottom",
    },
    {
      target: "[data-tour='order-detail']",
      title: "Order Details",
      description: "The expanded view shows everything about this order — date, total value, status, color coding, and payment schedule. Each payment term generates an expense entry on your Cash Calendar. Toggle Detailed Control for supplier info, units, incoterms, customs duty, and marketplace assignment.",
      position: "top",
    },
    {
      target: "[data-tour='order-kanban']",
      title: "Board View",
      description: "All your orders organized in a Kanban board — Pending, In Transit, Arrived, and Complete. Each card shows the order name, batch info, container count, units, and next payment due. Click 'Edit Order' on any card to jump to its full detail.",
      position: "top",
      onEnter: () => {
        // Switch to kanban/board view
        const toggleBtns = document.querySelectorAll("[data-tour='order-view-toggle'] button");
        if (!toggleBtns.length) {
          // Try the non-timeline header toggle
          const allBtns = document.querySelectorAll("button");
          for (const b of allBtns) {
            if (b.textContent.includes("Board")) { b.click(); break; }
          }
        } else {
          for (const b of toggleBtns) {
            if (b.textContent.includes("Board")) { b.click(); break; }
          }
        }
      },
    },
  ],

  transactions: [
    {
      target: "[data-tour='filter-recurring']",
      title: "Filters & Add Buttons",
      description: "Filter by type — All, Income, Expenses, Partners, Credit Lines, or Shared items. On the right, quickly add new entries: + Income, + Expense, + Partner, + Credit Line, or + VAT Payment (a pre-configured quarterly EU VAT template).",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='search-recurring']",
      title: "Search",
      description: "Search across all recurring transactions and partners by name. Results filter in real-time as you type — useful when you have many items and need to find a specific one quickly.",
      position: "bottom",
    },
    {
      target: "[data-tour='recurring-list']",
      title: "Recurring Items",
      description: "All your scheduled money flows — rent, salaries, subscriptions, partner payments, credit lines. Each entry generates transactions on your Cash Calendar every month it's active. Click any item to expand and edit amount, schedule, active months, growth rate, and more.",
      position: "top",
    },
  ],

  planner: [
    { target: "[data-tour='planner-filters']", title: "Search & Filters", description: "Search decisions by name or filter by category. Each icon represents a category — products, marketing, hiring, finance, operations, etc. Click 'All' to see everything, or tap a category to focus. You can add custom categories with '+ Category'.", position: "bottom" },
    { target: "[data-tour='add-decision']", title: "Add Decision", description: "Create a new business decision — launching a product, hiring, taking a loan, expanding to a new marketplace. Each decision gets a date, category, and financial impact that flows into your cashflow chart automatically.", position: "bottom" },
    { target: "[data-tour='planner-timeline']", title: "Timeline", description: "Your decisions laid out on a visual timeline. Scroll horizontally to navigate through weeks and months. Each dot is a decision — cards above and below the line show details. The green line shows your projected cash balance. Switch between Day, Week, Month, and Quarter views.", position: "top" },
    { target: "[data-tour='planner-card']", title: "Decision Card", description: "Each card shows the decision name, category, date, and status (Planned/Active/Completed). Click to edit. Use the link icon to connect related decisions, the warning icon for alerts, Edit to modify, and the trash icon to delete.", position: "top" },
  ],

  adjustments: [
    {
      target: "[data-tour='add-modifier']",
      title: "Add Modifier",
      description: "Create a new modifier — choose a type (Payout adjusts income per unit, Units adjusts sales volume), enter a percentage change (+20% or -15%), select which months it applies to, and optionally restrict it to a specific marketplace or product.",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='modifier-list']",
      title: "Modifiers List",
      description: "All your active modifiers — temporary percentage adjustments that overlay on your base data. Use them for seasonal boosts, payout changes, promotions, or what-if scenarios without editing your permanent marketplace settings. Click any modifier to expand and edit its details. The cashflow chart updates immediately.",
      position: "top",
    },
  ],

  cashflow: [
    {
      target: "[data-tour='calendar-months']",
      title: "Month Selector",
      description: "All 12 months at a glance. Each card shows total income (green) and expenses (red) with proportional bars. The selected month is highlighted — click any month to view its transactions below. In Multiple mode you can select several months at once.",
      position: "bottom",
      onEnter: () => {
        // Ensure month strip is visible (not in custom mode) and scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
        const monthBtn = document.querySelector("[data-tour='calendar-months']");
        if (!monthBtn) {
          // Month strip hidden — likely in custom mode, click "Month" pill to switch
          const pills = document.querySelectorAll("button");
          for (const b of pills) {
            if (b.textContent.trim() === "Month") { b.click(); break; }
          }
        }
      },
    },
    {
      target: "[data-tour='add-transaction']",
      title: "Add Transaction",
      description: "Add a one-time income or expense — a refund, an ad-hoc bill, a bonus payment, a loan, or any manual entry. These appear alongside your recurring items, order payments, and marketplace payouts in the tables below.",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='calendar-income']",
      title: "Income",
      description: "All income for the selected period — marketplace payouts, recurring income, manual entries, and planner-generated income. Each row shows the amount and payment status. The total row at the bottom includes a percentage change vs the previous period.",
      position: "top",
    },
    {
      target: "[data-tour='calendar-expenses']",
      title: "Expenses",
      description: "All expenses for the selected period — supplier order payments, recurring costs, fulfilment fees, and manual entries. Mark items as Paid to track real cashflow vs projected. Use the action dropdown for delay, partial payment, or deletion.",
      position: "top",
    },
  ],

  settings: [
    { target: "[data-tour='currency']", title: "Currency", description: "Choose Euro or US Dollar. This changes the display symbol everywhere — it doesn't convert values. Enter all your numbers in one consistent currency.", position: "bottom" },
    { target: "[data-tour='visible-years']", title: "Visible Years", description: "Which years appear across the app — Targets, Calendar, Chart. Add future years to extend your forecast horizon. The current year is always visible.", position: "bottom" },
  ],

  export: [
    {
      target: "[data-tour='export-buttons']",
      title: "Export Data",
      description: "Download your cashflow data in various formats. Full CSV Export gives you everything in a spreadsheet-friendly file. Full JSON Export is a complete data dump for backups or GDPR requests. Individual exports let you download just Monthly Cashflow, Recurring Expenses, Income, Orders, or Marketplaces separately.",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='import-section']",
      title: "Import Data",
      description: "Upload a CSV file to bulk-import recurring expenses, income, or transaction data. The system auto-detects columns and lets you review and map them before importing. Duplicates are automatically skipped — great for migrating from spreadsheets or other tools.",
      position: "top",
      onEnter: () => {
        const el = document.querySelector("[data-tour='import-section']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      },
    },
  ],

  help: [
    { target: "[data-tour='help-search']", title: "Search Everything", description: "Type any keyword to instantly search across all help articles. Results highlight in real-time as you type — payout delay, runway, payment terms, anything.", position: "bottom" },
    { target: "[data-tour='help-categories']", title: "Browse by Category", description: "Click any category pill to jump directly to that section. Each category covers one area of the app — Dashboard, Targets, Marketplaces, Orders, and more.", position: "bottom" },
    { target: "[data-tour='help-articles']", title: "Help Articles", description: "Each article explains a feature in detail — what it does, how it connects to your cashflow, and tips for getting the most out of it. Click to expand any article.", position: "top" },
  ],

  profitability: [
    {
      target: "[data-tour='profit-mode-toggle']",
      title: "Payout vs Extended",
      description: "Payout mode shows the essentials: selling price, costs, payout, and profit per unit. Extended mode adds margin %, ROI, ROAS, and break-even units. Toggle anytime — your data stays the same, only the display changes.",
      position: "bottom",
      onEnter: () => { window.scrollTo({ top: 0, behavior: "smooth" }); },
    },
    {
      target: "[data-tour='profit-add-product']",
      title: "Add Product",
      description: "Create a new product card. Choose a template — Marketplace (includes platform fees), Self-fulfilled (includes your shipping), Reseller (wholesale model), or Custom (blank slate). Link it to a marketplace for live payout sync, or keep it standalone.",
      position: "bottom",
    },
    {
      target: "[data-tour='profit-card-list']",
      title: "Product Cards",
      description: "All your products grouped by marketplace. Each card shows per-unit economics as a cost waterfall — selling price minus each cost line equals your profit. Click any card to expand, edit cost lines, toggle items on/off for what-if scenarios, or add custom costs.",
      position: "top",
    },
  ],

  // ── Catalog (renamed from Profitability / Products) ───────────────────
  // Without this entry, App.jsx hides the Tour button on the Catalog page
  // (TourButton renders only when TOURS[page] exists), which used to shift
  // the navbar layout. Keep at least one step here so the slot stays.
  catalog: [
    { target: "[data-tour='nav-bar']", title: "Catalog",
      description: "Every product across every marketplace, one row per product. The pills at the top filter by marketplace — click All to see everything, click Sandbox to scratchpad new products without affecting cashflow.",
      position: "bottom" },
    { target: "[data-tour='tour-button']", title: "Guided Tour",
      description: "Replay this tour anytime. Every page has its own walkthrough — new features get added automatically.",
      position: "bottom" },
  ],

  // ── Tasks ─────────────────────────────────────────────────────────────
  tasks: [
    { target: "[data-tour='nav-bar']", title: "Tasks",
      description: "Lightweight per-account task list. Assign tasks to yourself or any team member — assignees get a WhatsApp / Telegram ping on creation and again in their daily morning briefing if the task is still open.",
      position: "bottom" },
    { target: "[data-tour='tour-button']", title: "Guided Tour",
      description: "Replay this tour anytime.",
      position: "bottom" },
  ],
};
